// src/components/BookList.js
import React, { useEffect, useState } from 'react';
import { db } from '../firebase';
import {
  collection,
  getDocs,
  query,
  where,
  orderBy,
  limit as qLimit,
  documentId,
} from 'firebase/firestore';
import BookCard from './BookCard';
import { isReservationActive, listReservationsByBookIds } from '../utils/reservations';

// допоміжне — ділити масив на пачки (для where in ... максимум 10 значень)
const chunk = (arr, size = 10) => {
  const out = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
};

const BOOK_TEMPLATES_CACHE_TTL_MS = 5 * 60 * 1000;
const bookTemplatesCache = new Map();

const getCachedTemplate = (id, nowTs = Date.now()) => {
  const key = String(id || '');
  if (!key) return null;
  const entry = bookTemplatesCache.get(key);
  if (!entry) return null;
  if (nowTs - entry.ts > BOOK_TEMPLATES_CACHE_TTL_MS) {
    bookTemplatesCache.delete(key);
    return null;
  }
  return entry.row || null;
};

const cacheTemplates = (templates = []) => {
  const ts = Date.now();
  templates.forEach((row) => {
    if (!row?.id) return;
    bookTemplatesCache.set(String(row.id), { ts, row });
  });
};

const loadTemplatesByIdsCached = async (ids = []) => {
  const uniqueIds = Array.from(new Set((ids || []).filter(Boolean).map((x) => String(x))));
  if (!uniqueIds.length) return [];

  const nowTs = Date.now();
  const out = [];
  const missing = [];

  uniqueIds.forEach((id) => {
    const cached = getCachedTemplate(id, nowTs);
    if (cached) out.push(cached);
    else missing.push(id);
  });

  for (const pack of chunk(missing, 10)) {
    if (!pack.length) continue;
    const snap = await getDocs(
      query(collection(db, 'bookTemplates'), where(documentId(), 'in', pack))
    );
    const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    cacheTemplates(rows);
    out.push(...rows);
  }

  return out;
};

const formatCafePreview = (cafes = []) => {
  if (!cafes.length) return '';
  const asText = (c) => [c.name, c.address].filter(Boolean).join(', ');
  const firstTwo = cafes.slice(0, 2).map(asText).filter(Boolean);
  if (cafes.length <= 2) return firstTwo.join('; ');
  return `${firstTwo.join('; ')}; і ще ${cafes.length - 2}`;
};

/**
 * Параметри:
 *  - onlyAvailable: показувати лише шаблони, де є наявність
 *  - limitCount: скільки карток показувати
 *  - orderByBorrowed: сортувати за популярністю (borrowCount)
 *  - filterByDistrict: якщо задано — показує книги з кафе у цьому районі
 *  - filterByCafeNames: якщо задано — показує книги лише з цих кав'ярень
 */
const BookList = ({
  onlyAvailable = false,
  limitCount = 8,
  orderByBorrowed = true,
  filterByDistrict = null,
  filterByCafeNames = null,
  category = null,
}) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);

      try {
        const enrichWithAvailability = async (templates, preloadedBookDocs = null) => {
          const limited = templates.slice(0, limitCount);
          const ids = limited.map((x) => x.id).filter(Boolean);
          if (!ids.length) return limited;

          const booksRaw = [];
          if (Array.isArray(preloadedBookDocs)) {
            preloadedBookDocs.forEach((d) => {
              const row = d?.data ? { id: d.id, ...d.data() } : d;
              booksRaw.push(row);
            });
          } else {
            for (const pack of chunk(ids, 10)) {
              const s = await getDocs(
                query(
                  collection(db, 'books'),
                  where('status', '==', 'available'),
                  where('templateId', 'in', pack)
                )
              );
              s.docs.forEach((d) => booksRaw.push({ id: d.id, ...d.data() }));
            }
          }

          const byTemplate = new Map();
          const reservationRows = await listReservationsByBookIds(booksRaw.map((b) => b.id));
          const reservedBookIds = new Set(
            reservationRows.filter((r) => isReservationActive(r)).map((r) => String(r.bookId || r.id))
          );
          booksRaw.forEach((b) => {
            if (reservedBookIds.has(String(b.id))) return;
            if (!b?.templateId || !ids.includes(b.templateId) || b.status !== 'available') return;
            const key = `${b.cafeName || ''}|||${b.cafeAddress || ''}`;
            if (!byTemplate.has(b.templateId)) byTemplate.set(b.templateId, new Map());
            const cafeMap = byTemplate.get(b.templateId);
            if (!cafeMap.has(key)) {
              cafeMap.set(key, { name: b.cafeName || '', address: b.cafeAddress || '' });
            }
          });

          const withAvailability = limited.map((t) => {
            const cafes = Array.from((byTemplate.get(t.id) || new Map()).values());
            return {
              ...t,
              availabilityText: formatCafePreview(cafes),
              availabilityCafes: cafes,
            };
          });

          if (onlyAvailable) {
            return withAvailability.filter((t) => (t.availabilityCafes || []).length > 0);
          }
          return withAvailability;
        };

        // РЕЖИМ "ЗА КАТЕГОРІЄЮ"
        // -----------------------------------------------
        if (category) {
          // беремо найпопулярніші в категорії
          const snap = await getDocs(
            query(
              collection(db, 'bookTemplates'),
              where('category', '==', category),
              orderBy('borrowCount', 'desc'),
              qLimit(limitCount * 3) // трошки з запасом
            )
          );
          let list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
          cacheTemplates(list);
          if (onlyAvailable) list = list.filter(x => (x.availableCount ?? 0) > 0);
          // дедуп за isbn/id і обрізання до limitCount
          const seen = new Set(); const unique = [];
          for (const t of list) {
            const key = t.isbn || t.id;
            if (seen.has(key)) continue;
            seen.add(key);
            unique.push(t);
            if (unique.length === limitCount) break;
          }
          setItems(await enrichWithAvailability(unique));
          return;
        }
        // -----------------------------------------------
        // РЕЖИМ "КНИГИ З КАФЕ У ВКАЗАНОМУ РАЙОНІ"
        // -----------------------------------------------
        if (Array.isArray(filterByCafeNames) && filterByCafeNames.length > 0) {
          const cafeNames = filterByCafeNames.filter(Boolean);

          const bookDocs = [];
          for (const pack of chunk(cafeNames, 10)) {
            const qBooks = query(
              collection(db, 'books'),
              where('status', '==', 'available'),
              where('cafeName', 'in', pack)
            );
            const s = await getDocs(qBooks);
            bookDocs.push(...s.docs.map((d) => ({ id: d.id, ...d.data() })));
          }
          if (bookDocs.length === 0) {
            setItems([]);
            return;
          }

          const reservationRows = await listReservationsByBookIds(bookDocs.map((b) => b.id));
          const reservedBookIds = new Set(
            reservationRows.filter((r) => isReservationActive(r)).map((r) => String(r.bookId || r.id))
          );
          const visibleBooks = bookDocs.filter((b) => !reservedBookIds.has(String(b.id)));
          const tplIds = Array.from(new Set(visibleBooks.map((b) => b.templateId).filter(Boolean)));

          let list = await loadTemplatesByIdsCached(tplIds);
          if (onlyAvailable) list = list.filter((x) => (x.availableCount ?? 0) > 0);
          list.sort((a, b) => (b.borrowCount ?? 0) - (a.borrowCount ?? 0));
          setItems(await enrichWithAvailability(list, visibleBooks));
          return;
        }

        if (filterByDistrict) {
          // 1) Знаходимо всі кафе з таким districtSlug
          const cafesSnap = await getDocs(
            query(collection(db, 'cafes'), where('districtSlug', '==', filterByDistrict))
          );
          const cafes = cafesSnap.docs.map((d) => d.data());
          const cafeNames = cafes.map((c) => c.cafeName).filter(Boolean);

          if (cafeNames.length === 0) {
            setItems([]);
            return;
          }

          // 2) Беремо доступні екземпляри книг у цих кафе (книги -> шаблони)
          const bookDocs = [];
          for (const pack of chunk(cafeNames, 10)) {
            const qBooks = query(
              collection(db, 'books'),
              where('status', '==', 'available'),
              where('cafeName', 'in', pack)
            );
            const s = await getDocs(qBooks);
            bookDocs.push(...s.docs.map((d) => ({ id: d.id, ...d.data() })));
          }
          if (bookDocs.length === 0) {
            setItems([]);
            return;
          }

          // 3) Витягуємо унікальні templateId та грузимо їх з bookTemplates
          const reservationRows = await listReservationsByBookIds(bookDocs.map((b) => b.id));
          const reservedBookIds = new Set(
            reservationRows.filter((r) => isReservationActive(r)).map((r) => String(r.bookId || r.id))
          );
          const visibleBooks = bookDocs.filter((b) => !reservedBookIds.has(String(b.id)));
          const tplIds = Array.from(new Set(visibleBooks.map((b) => b.templateId).filter(Boolean)));

          let list = await loadTemplatesByIdsCached(tplIds);

          // якщо треба — перестраховка за наявністю
          if (onlyAvailable) {
            list = list.filter((x) => (x.availableCount ?? 0) > 0);
          }

          // 4) Сортуємо за популярністю, обрізаємо
          list.sort((a, b) => (b.borrowCount ?? 0) - (a.borrowCount ?? 0));
          setItems(await enrichWithAvailability(list, visibleBooks));
          return;
        }

        // -----------------------------------------------
        // ЗВИЧАЙНИЙ РЕЖИМ "ПОПУЛЯРНІ ШАБЛОНИ"
        // (без вимоги композитних індексів)
        // -----------------------------------------------
        // Щоб не вимагати composite-index (where+orderBy по різних полях),
        // спочатку беремо трохи більшу вибірку і фільтруємо на клієнті.
        const fetchSize = limitCount * 5;

        let qTpl = query(collection(db, 'bookTemplates'));
        if (orderByBorrowed) {
          qTpl = query(qTpl, orderBy('borrowCount', 'desc'), qLimit(fetchSize));
        } else {
          qTpl = query(qTpl, qLimit(fetchSize));
        }

        const snap = await getDocs(qTpl);
        let base = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        cacheTemplates(base);

        if (onlyAvailable) {
          base = base.filter((x) => (x.availableCount ?? 0) > 0);
        }

        // якщо ми не тягнули orderBy з БД — можна відсортувати тут
        if (!orderByBorrowed) {
          base.sort((a, b) => (b.borrowCount ?? 0) - (a.borrowCount ?? 0));
        }

        // 🔧 Дедуп: за ISBN (якщо є), інакше за id
        const seen = new Set();
        const unique = [];
        for (const t of base) {
          const key = t.isbn || t.id;
          if (seen.has(key)) continue;
          seen.add(key);
          unique.push(t);
        }

        setItems(await enrichWithAvailability(unique)); // ✅ ЄДИНИЙ setItems

      } finally {
        setLoading(false);
      }
    })();
  }, [onlyAvailable, limitCount, orderByBorrowed, filterByDistrict, filterByCafeNames, category]);

  const [winW, setWinW] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200);
  useEffect(() => {
    const onResize = () => setWinW(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  const cols =
    winW <= 540 ? 'repeat(2, minmax(0,1fr))' :
    winW <= 900 ? 'repeat(3, minmax(0,1fr))' :
                  'repeat(4, minmax(0,1fr))';

  if (loading) return null;

  

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: cols,
        gap: 16,
        marginBottom: 24,
        alignItems: 'stretch',
        justifyItems: 'stretch'
      }}
    >


      {items.map((it) => (
        <BookCard
          key={it.id}
          isbn={it.isbn}
          title={it.title}
          author={it.author}
          coverUrl={it.coverUrl}
          price={it.price}
          borrowPrice={it.borrowPrice}
          borrowCount={it.borrowCount}
          availabilityText={it.availabilityText}
          availabilityCafes={it.availabilityCafes}
        />
      ))}

      {items.length === 0 && (
        <div style={{ color: '#6b7280' }}>Книг поки немає.</div>
      )}
    </div>
  );
};

export default BookList;
